import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEO from '@/components/feature/SEO';
import VideoCard from '@/pages/home/components/VideoCard';
import { allVideos, categories } from '@/mocks/videos';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const video = allVideos.find((v) => v.id === id);

  if (!video) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="max-w-[1400px] mx-auto px-4 md:px-6 pt-20 pb-20 text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-3xl text-foreground-500"></i>
          </div>
          <h2 className="text-xl font-bold text-foreground-200 mb-2">未找到该内容</h2>
          <p className="text-foreground-500 mb-6">该视频可能已下架或链接有误</p>
          <Link
            to="/"
            className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-semibold transition-all whitespace-nowrap inline-block"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const relatedVideos = allVideos
    .filter((v) => v.categorySlug === video.categorySlug && v.id !== video.id)
    .slice(0, 6);

  const detailSEO = {
    title: `${video.title} - 棒棒TV | 高清免费在线观看`,
    description: `棒棒TV为您提供${video.title}高清免费在线观看。${video.description.slice(0, 120)}...`,
    keywords: `${video.title},${video.category},${video.tags.join(',')},棒棒TV,在线观看,高清视频`,
    canonicalUrl: `https://bangbangtv.com/detail/${video.id}`,
    ogType: 'video.movie',
    ogImage: video.poster,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Movie',
        name: video.title,
        url: `https://bangbangtv.com/detail/${video.id}`,
        image: video.poster,
        description: video.description,
        dateCreated: `${video.year}`,
        director: { '@type': 'Person', name: video.director },
        actor: video.actors.map((a) => ({ '@type': 'Person', name: a })),
        countryOfOrigin: { '@type': 'Country', name: video.region },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://bangbangtv.com' },
          { '@type': 'ListItem', position: 2, name: video.category, item: `https://bangbangtv.com/category/${video.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: video.title },
        ],
      },
    ],
  };

  const categoryInfo = categories.find((c) => c.slug === video.categorySlug);

  return (
    <>
      <SEO data={detailSEO} />
      <ScrollToTop />
      <Navbar />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: video.category, href: `/category/${video.categorySlug}` },
            { label: video.title },
          ]}
        />

        <article className="max-w-[1400px] mx-auto px-4 md:px-6 pb-12">
          <div className="bg-background-100 rounded-xl overflow-hidden">
            <div className="flex flex-col md:flex-row">
              <div className="w-full md:w-[300px] lg:w-[350px] flex-shrink-0">
                <div className="aspect-[2/3] md:h-full">
                  <img
                    src={video.poster}
                    alt={video.title}
                    className="w-full h-full object-cover object-top"
                    title={`${video.title} - 棒棒TV高清在线观看`}
                  />
                </div>
              </div>

              <div className="flex-1 p-5 md:p-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="px-3 py-1 rounded-full bg-accent-500 text-white text-xs font-semibold whitespace-nowrap">
                    {video.category}
                  </span>
                  <span className="text-sm text-foreground-400">{video.year}</span>
                  <span className="flex items-center gap-1 text-accent-400 text-sm">
                    <i className="ri-star-fill"></i>
                    <strong>{video.rating}</strong>
                  </span>
                  <span className="px-2 py-0.5 rounded bg-secondary-500/20 text-secondary-400 text-xs whitespace-nowrap">
                    {video.status}
                  </span>
                </div>

                <h1 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading mb-3">
                  {video.title}
                </h1>

                <div className="flex items-center gap-2 text-sm text-foreground-400 mb-4 flex-wrap">
                  <span>更新至：{video.updateInfo}</span>
                  <span>·</span>
                  <span>{video.views.toLocaleString()} 次播放</span>
                </div>

                <p className="text-sm md:text-base text-foreground-300 leading-relaxed mb-6">
                  {video.description}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="text-foreground-500 whitespace-nowrap">导演：</span>
                    <span className="text-foreground-300">{video.director}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-foreground-500 whitespace-nowrap">地区：</span>
                    <span className="text-foreground-300">{video.region}</span>
                  </div>
                  <div className="flex items-start gap-2 col-span-1 sm:col-span-2">
                    <span className="text-foreground-500 whitespace-nowrap">主演：</span>
                    <span className="text-foreground-300">{video.actors.join(' / ')}</span>
                  </div>
                  <div className="flex items-start gap-2 col-span-1 sm:col-span-2">
                    <span className="text-foreground-500 whitespace-nowrap">集数：</span>
                    <span className="text-foreground-300">
                      {typeof video.episodes === 'number' ? `${video.episodes}集` : video.episodes}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap mb-6">
                  {video.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-background-200 text-foreground-400 text-xs whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <Link
                  to={`/category/${video.categorySlug}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-semibold transition-all whitespace-nowrap"
                >
                  <i className="ri-play-fill text-lg"></i>立即播放
                </Link>
              </div>
            </div>
          </div>

          {relatedVideos.length > 0 && (
            <section className="mt-10">
              <h2 className="text-xl font-bold text-foreground-100 font-heading mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {relatedVideos.map((v) => (
                  <VideoCard key={v.id} video={v} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}